/**
 * Document Management Component
 * 
 * Core document/contract management system providing comprehensive CRUD operations,
 * real-time processing status polling, advanced filtering, and modal-based workflows.
 * 
 * Features:
 * - Upload and manage contracts with file validation (max 20MB PDF files)
 * - Real-time polling for contract processing status (30-second intervals)
 * - Advanced multi-field filtering (Document Type, Licensor, Status, Text Search)
 * - DataTable integration with 12+ columns, sorting, pagination, and responsive layout
 * - Modal workflows: Create (Upload), Read (View), Update (Edit), Delete (Confirm)
 * - Master document management for contract amendments
 * - Access control with role-based page access validation
 * - Error tracking and user notifications via toast messages
 * - Dynamic form validation with real-time error feedback
 * 
 * State Management:
 * - Document data from API (disney-contract-list/ endpoint)
 * - Modal states: add/edit/delete document modals
 * - Form data: licensor, contract number, document type, file uploads
 * - Filter state: Document type, Licensor, Status, Text search
 * - Dropdown options: Loaded from licensors/, documents/master/, documents/types/ APIs
 * - Loading state for async operations
 * 
 * Polling System:
 * - Monitors contracts with "Running" status (max 50 attempts at 30-second intervals)
 * - Stops polling on "Success" or when max attempts reached
 * - Triggers data refresh when processing completes
 * 
 * DataTable Columns (12 visible):
 * 1. Document Number
 * 2. Document Name (clickable link)
 * 3. Document Type
 * 4. Licensor
 * 5. Start Date (effectivedate)
 * 6. Expiration Date
 * 7. Sell-off Period
 * 8. Payable Contact
 * 9. Processing Status (badge: Success/Failed/Processing)
 * 10. Import Date
 * 11. First Imported By
 * 12. Actions (View, Delete with tooltips)
 * 
 * @component
 * @returns {JSX.Element} Complete document management interface with data table, filters, and modals
 * 
 * @requires react
 * @requires react-bootstrap (Modal, Tooltip, OverlayTrigger)
 * @requires react-select (multi-select dropdowns with custom checkbox components)
 * @requires datatables.net (advanced table features)
 * @requires jquery (DOM manipulation for DataTable)
 * @requires react-router-dom (useNavigate for navigation)
 * @requires axios (file upload multipart/form-data)
 */

import React, { useEffect, useState } from "react";
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { default as Select, components }  from "react-select";
import $ from "jquery";
import * as ReactDOM from 'react-dom';
import "datatables.net";
import { useNavigate,useHistory } from "react-router-dom";
import axios from 'axios';
import {getFilenameFromURL,getUsername,replaceSpacesWithUnderscores,capitalizeFirstLetterOfEachWord} from "../../utils/common_functions"
import {API_SERVER_URL} from "../../utils/config_ip_details";
import {API_SUCCESS_RESPONSE_CODE,API_SUCCESS_RESPONSE_CREATED_CODE,CONTRACT_POLLING_MAX_COUNT,CONTRACT_STATUS_CHECK_POLLING_DURATION} from "../../utils/config_mapping"
import {apiCall} from "../../utils/server_utils"
import CommonIcon from '../common/customicon';
import PageLoader from '../common/pageloader';
import ProcessLoader from '../common/processloader';
import "../../../node_modules/datatables.net-dt/css/dataTables.dataTables.css";
import {trackError} from "../../utils/error_tracker"
import {OVERVIEW_META_DATA} from "../../utils/config_mapping";
import Message from "../common/message";
import { hasPageAccess,accessDenied,AccessDeniedModal,validatePageAccess } from "../../utils/page_access"; // Import
import HeaderLogo  from "../structure/headerlogo";
/**
 * Document Component - Core document/contract management page
 * 
 * Renders:
 * - Header with "Contract List" title and branding
 * - Search bar and action buttons (Refresh, Upload)
 * - Advanced filter row (Document Type, Licensor, Status multi-selects)
 * - DataTable with 12 columns displaying all contracts
 * - Delete confirmation modal
 * - Upload new contract modal (with file drop zone)
 * - Edit contract modal (Licensor and Contract No only)
 * - Access control validation and error messages
 * 
 * @component
 * @returns {JSX.Element} Full document management UI
 */
export const Document = () => {
  // ========== POLLING STATE ==========
  // Manages real-time status polling for contracts in "Running" state
  let pollingInterval = null; // Store the interval ID for polling to enable cleanup
  let pollingCount = 0; // Track the number of polling attempts (max 50 per CONTRACT_POLLING_MAX_COUNT)
  let pageName = "Documents"; // Page identifier for access control validation

  const [accessModal, setAccessModal] = useState({ isOpen: false });

  /**
   * Custom Option Component for React-Select with Checkboxes
   * 
   * Renders checkbox options for multi-select dropdowns (Document Type, Licensor, Status)
   * Provides visual feedback with checkmarks for selected items
   * 
   * @component
   * @param {Object} props - React-Select Option props
   * @param {string} props.label - Display label for the option
   * @param {boolean} props.isSelected - Whether this option is selected
   * @returns {JSX.Element} Checkbox with label
   */
  const Option = (props) => {
    return (
      <div>
        <components.Option {...props}>
          <input
            type="checkbox"
            checked={props.isSelected}
            onChange={() => null}
          />{" "}
          <label>{props.label}</label>
        </components.Option>
      </div>
    );
  };

  // ========== MODAL STATE MANAGEMENT ==========
  // Add Document Modal - Triggered by "Upload" button
  const [adddocumentmodal, setAddDocumentModal] = useState(false);
  const handleClose = () => setAddDocumentModal(false);
  const handleShow = () => setAddDocumentModal(true);

  // Edit Document Modal - Triggered by edit button on table row
  const [editdocumentmodal, setEditDocumentModal] = useState(false);
  const handleEditClose = () => {setEditDocumentModal(false);$("#contract_grid tbody tr").removeClass("highlight-row");} 
  const handleEditShow = () => setEditDocumentModal(true);

  // Delete Document Modal - Triggered by delete button on table row with confirmation
  const [deletedocumentmodal, setDeleteDocumentModal] = useState(false);
  const handleDeleteClose = () => setDeleteDocumentModal(false);
  const handleDeleteShow = () => setDeleteDocumentModal(true);

  // ========== DOCUMENT DATA STATE ==========
  const [loading, setLoading] = useState(true); // Loading spinner state during data fetch
  const [data, setData] = useState([]); // All document records from API
  const [licensorOptions, setLicensorOptions] = useState([]); // Dropdown options from licensors/ API
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]); // Dropdown options from documents/types/ API
  const [masterDocumentOptions, setMasterDocumentOptions] = useState([]); // Master documents for amendment linking
  
  // Status options for filter dropdown (Success, Error, Processing, Queued)
  const statusoptions =[{label:"Success",value:"Success"},{label:"Error",value:"Error"},{label:"Processing",value:"Running"},{label:"Queued",value:"Queued"}]
  
  const [filteredData, setFilteredData] = useState([]); // Data after applying all active filters
  const [searchQuery, setSearchQuery] = useState(""); // Current search text (deprecated, replaced with filterTextSearch)
  
  // ========== FILTER STATE ==========
  // Stores all active filter selections - multi-select arrays and text search
  const [filters, setFilters] = useState({
    filterdocumentType: null, // Array of {label, value} for Document Type
    filterlicensor: null, // Array of {label, value} for Licensor
    filterexpiryDate: "", // Date string for expiration date filter (currently unused)
    filterstatus: null, // Array of {label, value} for Processing Status
    filterTextSearch: "" // Free-text search across all document fields
  });

  // ========== FORM STATE FOR DOCUMENT UPLOAD/EDIT ==========
  // Initial form state reset template
  const initialState = {
    licensor: "", // Selected licensor {label, value}
    documentType: null, // Selected document type {label, value}
    masterFile: "", // Selected master document (for amendments)
    documentFile: null, // Actual file object from input
    contractNo:"", // User-entered contract number
    _id:"" // Document ID (for edit operations)
  }
  
  const [formData, setFormData] = useState(initialState); // Current form data state
  const [errors, setErrors] = useState({}); // Form validation errors
  const navigate = useNavigate(); // React Router navigation hook
  
  // ========== FALLBACK DATA FOR API FAILURES ==========
  // Used when API endpoints are unavailable to prevent empty UI
  const DUMMY_LICENSORS = [
    { value: "Licensor A", label: "Licensor A" },
    { value: "Licensor B", label: "Licensor B" }
  ];
  const DUMMY_DOCUMENT_TYPES = [
    { value: "Agreement", label: "Agreement" },
    { value: "Amendment", label: "Amendment" }
  ];
  const DUMMY_CONTRACTS = [
  ];

  // ========== DROPDOWN DATA INITIALIZATION ==========
  /**
   * Fetch Licensor, Master Document, and Document Type data on component mount
   * 
   * Calls three endpoints in parallel:
   * 1. licensors/ - Get available licensors for dropdown
   * 2. documents/master/ - Get master documents for amendment linking
   * 3. documents/types/ - Get document types for classification
   * 
   * Falls back to DUMMY data if any API call fails
   * Uses error tracking for invalid responses
   * 
   * @async
   * @function fetchDropdownData
   */
  useEffect(() => {
      const fetchDropdownData = async () => {
        try {
          // ========== FETCH LICENSORS ==========
          let apiUrl = API_SERVER_URL+ 'licensors/'
          let method = "GET"
          const result = await apiCall(method,apiUrl);
          if(result!==undefined){
          if (result.code === API_SUCCESS_RESPONSE_CODE) {
            setLicensorOptions([
              // { value: "", label: "Select a Licensor" }, // Add an empty option
              ...result.data.map((item, i) => ({
                value: item.licensorName,
                label: item.licensorName,
              })),
            ]);
          } else {
            trackError("The API response is invalid:<br/>URL:"+apiUrl+"<br/>Method:"+method+"<br>", JSON.stringify(result))
          }
          } else {
            // Fallback when API not available
            setLicensorOptions(DUMMY_LICENSORS);
          }
   

         // ========== FETCH MASTER DOCUMENTS (FOR AMENDMENTS) ==========
        try {
         let apiMasterDocumentUrl = API_SERVER_URL+ 'documents/master/'
          let methodMasterDocument = "GET"
          let resultMasterDocuemnts = await apiCall(methodMasterDocument,apiMasterDocumentUrl);
          
          if (resultMasterDocuemnts.code === API_SUCCESS_RESPONSE_CODE) {

          

            setMasterDocumentOptions([
              // { value: "", label: "Select a Licensor" }, // Add an empty option
              ...resultMasterDocuemnts.data.map((item, i) => ({
                value: item.DocumentNo,
                label: getFilenameFromURL(item.FilePath),
                licensor: item.licensor
              })),
            ]);
          } else {
             trackError("The API response is invalid:<br/>URL:"+apiMasterDocumentUrl+"<br/>Method:"+methodMasterDocument+"<br>", JSON.stringify(resultMasterDocuemnts))
          }
        
        } catch (error) {
          console.error("Error fetching dropdown data:", error);
        } 

        // ========== FETCH DOCUMENT TYPES ==========
           let apiDocumentTypeUrl = API_SERVER_URL+ 'documents/types/'
           let methodDocumentType = "GET"
           let resultDocumentType = await apiCall(methodDocumentType,apiDocumentTypeUrl);
          if(resultDocumentType!==undefined){
          if (resultDocumentType.code === API_SUCCESS_RESPONSE_CODE) {
            setDocumentTypeOptions([
              // { value: "", label: "Select a Licensor" }, // Add an empty option
              ...resultDocumentType.data.map((item, i) => ({
                value: item.documentTypeName,
                label: item.documentTypeName,
              })),
            ]);
          } else {
            trackError("The API response is invalid:<br/>URL:"+apiDocumentTypeUrl+"<br/>Method:"+methodDocumentType+"<br>", JSON.stringify(resultDocumentType))
          }
          } else {
            // Fallback when API not available
            setDocumentTypeOptions(DUMMY_DOCUMENT_TYPES);
          }
        } catch (error) {
          console.error("Error fetching dropdown data:", error);
          // On error use dummy dropdowns
          setLicensorOptions(DUMMY_LICENSORS);
          setDocumentTypeOptions(DUMMY_DOCUMENT_TYPES);
        }
      };
      fetchDropdownData();
    }, []); // Runs only once on component mount


    // ========== VIEW HANDLER ==========
    /**
     * Navigate to document details view page
     * 
     * Used when user clicks "View" button on table row
     * Routes to document-details/:id/view path with document ID
     * 
     * @function
     * @param {string} id - Document ID (_id from database)
     * @param {string} name - File path to document
     */
    const handleView = (id,name) => {
       const fileName = getFilenameFromURL(name);
       navigate("/document-details/"+id+"/view")
        console.log("View button clicked for ID:", id);
    };

    // ========== ASK GENIE HANDLER ==========
    /**
     * Navigate to AI document analysis page
     * 
     * Routes to document-details/:id/askgenie path for AI-powered document analysis
     * Currently disabled in UI (commented out in modal)
     * 
     * @function
     * @param {string} id - Document ID
     * @param {string} name - File path to document
     */
    const handleAskgenie = (id,name) => {
      const fileName = getFilenameFromURL(name);
      navigate("/document-details/"+id+"/askgenie")
       console.log("View button clicked for ID:", id);
   };

    // ========== DELETE HANDLER ==========
    /**
     * Delete a contract document via API
     * 
     * Calls contracts/:id/delete/ POST endpoint
     * On success: closes modal, refreshes data table
     * On failure: tracks error with error_tracker utility
     * 
     * @async
     * @function
     * @param {Event} event - Click event from delete button
     */
    const handleDelete = async (event) => {
      const dataId = event.target.getAttribute('data-id');
      if(dataId){
        let apiUrl = API_SERVER_URL+ 'contracts/'+dataId+'/delete/'
        let method = "POST"
        try {
            const result = await apiCall(method,apiUrl);
            if(result!==undefined){
            if (result.code === API_SUCCESS_RESPONSE_CODE) {
              handleDeleteClose()
              fetchData();
            } else {
              trackError("The API response is invalid:<br/>URL:"+apiUrl+"<br/>Method:"+method+"<br>", JSON.stringify(result))
            }
            }
        } catch (error) {
          console.error("Error refreshing token:", error);
        }
      }
    };

    // ========== FETCH ALL DOCUMENTS ==========
    /**
     * Fetch all contract documents from backend
     * 
     * Calls disney-contract-list/ GET endpoint to retrieve all documents
     * Sets master documents, filters data, and initiates polling if needed
     * Handles "Running" status documents with status polling
     * Fallback to DUMMY_CONTRACTS on API failure
     * 
     * @async
     * @function
     */
    const fetchData = async () => {
      $(".page_pro_load").hide();
      setLoading(true); // Stop loading spinner
      try {
        let result = await apiCall("GET", `${API_SERVER_URL}disney-contract-list/`);

        if (result && result.code === API_SUCCESS_RESPONSE_CODE) {
          setMasterDocumentOptions(
            result.data.map((item) => ({
              value: item.DocumentNo,
              label: getFilenameFromURL(item.FilePath),
              licensor: item.licensor,
            }))
          );

        // Extract document IDs where StatusCode is "Running"
        const ids = result.data
        .filter(item => item.status && (item.status.StatusCode === "Running" || item.status.StatusCode === "running"))
        .map(item => item._id);
      
        // If there are IDs with StatusCode "Running", start polling for status
        if (ids.length > 0) {
          const id_comma_separated = ids.join(',');
          pollForStatus(id_comma_separated);
        }
          setData(result.data);
          setFilteredData(result.data); // Set initial data for filtering
         
        } else {
          // Fallback to dummy data when API not loaded or response invalid
          console.warn("API unavailable or returned invalid data, using dummy data.");
          setMasterDocumentOptions(DUMMY_CONTRACTS.map(item => ({
            value: item.DocumentNo,
            label: getFilenameFromURL(item.FilePath),
            licensor: item.licensor
          })));
          setData(DUMMY_CONTRACTS);
          setFilteredData(DUMMY_CONTRACTS);
        }
      }
      finally {
         setLoading(false); // Stop loading spinner
     }
    };
    
    // ========== INITIAL DATA FETCH ==========
    // Fetch data once on component mount
    useEffect(() => {
      fetchData();
 }, []); // Empty dependency array ensures this runs only once
  

 


 const pollForStatus = (id_comma_separated) => {
  // Clear any previous interval to prevent multiple polling instances
  if (pollingInterval) {
    clearInterval(pollingInterval);
  }
  pollingCount = 0; // Reset polling count before starting
  // Start polling every 30 seconds
  pollingInterval = setInterval(async () => {
    if (pollingCount >= CONTRACT_POLLING_MAX_COUNT) {
      console.warn("Max polling attempts reached. Stopping polling.");
      clearInterval(pollingInterval);
      return;
    }

    try {
      const result_status_check = await apiCall("GET", `${API_SERVER_URL}contracts/status/?id_list=` + id_comma_separated);

      if (result_status_check.code === API_SUCCESS_RESPONSE_CODE) {
        // Check if any of the statuses is "Success"
        const successFound = result_status_check.data.some(item => item.status.StatusCode === "Success");
        if (successFound) {
          console.log("Success found! Fetching data...");
          clearInterval(pollingInterval); // Stop polling once success is found
          await fetchData(); // Call fetchData when "Success" is found
        }
      } else {
        console.warn("Unexpected response status in status check:", result_status_check.status);
      }
    } catch (error) {
      console.error("Error during status check:", error);
    }

    pollingCount++; // Increment polling count after each attempt
  }, CONTRACT_STATUS_CHECK_POLLING_DURATION); // Poll every 30 seconds
};



  useEffect(() => {
    if (filteredData.length > -1) {


      var updateScrollY = function() {
    
        window.location.reload();
        return;

        // Calculate new scrollY based on dynamic height adjustments
    var sidebarHeight = $(".sidebar-background").height(); // Dynamically get sidebar height
    var windowHeight = $(window).height(); // Get current window height
    var tableOffsetTop = $("#contract_grid").offset().top; // Get table's top offset
    var newScrollY = windowHeight - tableOffsetTop - 20; // Adjust for padding/margin

    // Ensure the value is reasonable
    newScrollY = Math.max(newScrollY, 100); // Minimum height for usability

    console.log("Calculated scrollY:", newScrollY);

    // Update DataTable's scrollY setting
    var table = $("#contract_grid").DataTable();
    table.settings()[0].oScroll.sY = newScrollY + "px";
    table.draw();



    };
    
    $(window).resize(function() {
       // updateScrollY(); // Recalculate and update scrollY on window resize
    });
    
      const table = $("#contract_grid").DataTable({
        scrollCollapse: true,
        //"dom": '<"top"f>rt<"bottom"ilp><"clear">',
       //dom: '<"top ilfp">rt<"clear">',
       dom: '<"top d-flex justify-content-between algn-items-center"ilfp>rt<"clear">',
        scrollX: true,
        "aLengthMenu": [[5,10,50,100,500, -1], [5,10,50,100,500,"All"]],
        "pagingType": "simple_numbers",
        pageLength: 50, // Set default number of records per page
//        scrollY:$(window).height()-($("#table_height").height()+105),
        scrollY:$(".navbar-menu").height()-($("#table_height").height()),
        data: filteredData,
        destroy: true, // Ensure the previous table is destroyed
        order: [],  // This disables default sorting on any 
        columnDefs: [
          { 
            targets: '_all', // Left-align all other columns
            className: 'text-left' // Bootstrap's text-left class
          }
        ],
        columns: [
          { data: "DocumentNo", title: "DocumentNo",width: "40px", },
          {
            data: "FilePath",
            title: "Document Name",
            width: "200px",
            render: function (data, type, row) {
              return `<a href="javascript:void(0);" class="view-btn" data-id="${row._id}" data-name="${row.FilePath}" data-status="${row.status.StatusCode}">
                        ${getFilenameFromURL(data)} <i class="datatable-open-document-icon"></i
                      </a>`;
            }
          },
          { data: "documenttype", title: "Document Type", width: "10px" },
          // { data: "ContractID", title: "Contract ID", width: "40px"},
          { data: "licensor", title: "Licensor", width: "10px" },
          // {
          //   data: "status",
          //   title: OVERVIEW_META_DATA["LicensorLegalName"]["label"],
          //   width: "40px",
          //   render: function (data, type, row) {
          //      return row["items"]["LicensorLegalName"]["value"];
          //   }
          // },
          {
            data: "status",
            title: (OVERVIEW_META_DATA["startdate"]["label"]),
            width: "40px",
            render: function (data, type, row) {
               // guard item access
               return (row && row.effectivedate  ? row.effectivedate : "NA");
            }
          },
          {
            data: "status",
            title: (OVERVIEW_META_DATA["expirationdate"]["label"]),
            width: "40px",
            render: function (data, type, row) {
            return (row && row.expirationdate  ? row.expirationdate : "NA");}
          },
          {
            data: "status",
            title: (capitalizeFirstLetterOfEachWord(OVERVIEW_META_DATA["sell-off period"]["label"])),
            width: "40px",
            render: function (data, type, row) {
                return (row && row.sell_off_period  ? row.sell_off_period : "NA");
            }
          },
          {
            data: "status",
            title: (OVERVIEW_META_DATA["PayableContact"]["label"]),
            width: "40px",
            render: function (data, type, row) {
                return (row && row.payable_to  ? row.payable_to : "NA");
            }
          },
          {
            data: "status",
            title: "Processing Status",
            width: "40px",
            render: function (data, type, row) {
              // guard status access
              let status = (data && data.StatusCode) ? data.StatusCode : (row && row.status && row.status.StatusCode) ? row.status.StatusCode : 'unknown';
              status = (status || 'unknown').toString().toLowerCase();
              if (status === "success") {
                return `<span className="badge bg-success">Success</span>`;
              } else if (status === "error") {
                return `<span className="badge bg-danger">Failed</span>`;
              } else if (status === "running") {
                return `<span className="badge bg-warning">Processing</span>`;
              }
              else {
                return `<span className="badge bg-warning">`+capitalizeFirstLetterOfEachWord(status)+`</span>`;
              }
            }
          },
          { data: "importdate", title: "Imported Date", width: "10px" },
          { data: "importedby", title: "First Imported By", width: "40px" },
         // { data: "last_imported_date", title: "Last Imported Date", width: "40px" },
          //{ data: "last_imported_by", title: "Last Imported By", width: "40px" },
          //{ data: "importedby", title: "Error Date", width: "40px" },
          // { data: "last_edited_at", title: "LastEditedAt", width: "40px" },
          // { data: "last_edited_by", title: "LastEditedBy", width: "40px" },
          {
            title: "Actions&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;",
            orderable: false, // Disable sorting for this column
            data: null,
            width: "100px", 
            createdCell: function (td, cellData, rowData, row, col) {
              // Clear the default HTML and replace it with React-rendered content
              ReactDOM.render(
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <OverlayTrigger
                    placement="top"
                    trigger={['hover', 'click']} // Add 'click' to ensure it closes on click
                    overlay={<Tooltip id="view-top">View</Tooltip>}
                  >
                    <a href="javascript:void(0);" className="view-btn"  style={{color:"#05276d",fontSize:"11px"}} data-id={rowData._id} data-name={rowData.FilePath} data-status={rowData.status.StatusCode}>
                      {/* <i className="datatable-edit-icon" style={{color:"#05276d",fontSize:"11px"}}></i> */}
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="0.75" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-eye-icon lucide-eye"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/></svg>
                    </a>
                  </OverlayTrigger>
                  <OverlayTrigger
                    trigger={['hover', 'click']} // Add 'click' to ensure it closes on click
                    placement="top"
                    overlay={<Tooltip id="delte-top">Delete</Tooltip>}
                  >
                    <a href="javascript:void(0);" className="delete-btn"  style={{color:"#05276d",fontSize:"11px"}} data-bs-toggle="modal"
                        data-status={rowData.status.StatusCode} data-id={rowData._id} data-name={rowData.FilePath}>
                      {/* <i className="datatable-delete-icon" style={{color:"#05276d",fontSize:"11px"}}></i> */}
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="0.75" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-trash2-icon lucide-trash-2"><path d="M10 11v6"/><path d="M14 11v6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </a>
                  </OverlayTrigger>
                  {/* <OverlayTrigger
                    trigger={['hover', 'click']} // Add 'click' to ensure it closes on click
                    placement="top"
                    overlay={<Tooltip id="askgenie-top">Ask Genie</Tooltip>}
                  >
                  <a href="javascript:void(0);" className="askgenie-btn" data-id={rowData._id} data-name={rowData.FilePath} data-status={rowData.status.StatusCode}>
                    <i className="datatable-askgenie-icon"></i>
                  </a></OverlayTrigger> */}
                </div>,
                td // Render the content directly into the table cell
              );
            }
          }
        
        ],
        fixedColumns: {
          start: 1,
          end: 1
      },
        // fixedColumns: {
        //   leftColumns: 1,   // No columns fixed on the left
        //   rightColumns: 2   // Fixes 1 column on the right (Actions column)
        // },
        paging: true,
        searching: false,
        ordering: true,
        responsive: true,
        fixedHeader: true,
        language: {
          emptyTable: "No data found matching the filters",  // Custom message for no data
        },
      });

      // Attach click handlers again for view, delete, and askgenie buttons
      $("#contract_grid").on("click", ".view-btn", function () {
        const id = $(this).data("id");
        const name = $(this).data("name");
        const status = $(this).data("status");
         handleView(id, name);
        if(status==="Success" || status==="success"){
          handleView(id, name);
        }else {
          const targetElement = document.getElementById("app_err");
          if (targetElement) {
                    ReactDOM.unmountComponentAtNode(targetElement);
          }
          ReactDOM.render(<Message type='info' message="Please wait while the document is being processed." />, targetElement);
          
        }
      });

      $("#contract_grid").on("click", ".view-edit", function () {
        const status = $(this).data("status");
        if(status==="Success" || status==="success"){
        // Remove highlight from all rows
        $("#contract_grid tbody tr").removeClass("highlight-row");
        // Add highlight to the clicked row
        $(this).closest("tr").addClass("highlight-row");
        const id = $(this).data("id");
        const contractidid = $(this).data("contractid");
        const clientid = $(this).data("clientid");
        const licensor = $(this).data("licensor");
        const name = getFilenameFromURL($(this).data("name"));
        setFormData({
          licensor: {"label":licensor,"value":licensor},
          documentType: null,
          masterFile: "",
          documentFile: name,
          contractNo:contractidid,
          _id:id
        });
        setErrors({});
        handleEditShow()
        }else {
          const targetElement = document.getElementById("app_err");
          if (targetElement) {
                    ReactDOM.unmountComponentAtNode(targetElement);
          }
          ReactDOM.render(<Message type='info' message="Please wait while the document is being processed." />, targetElement);
          $("#contract_grid tbody tr").removeClass("highlight-row");

        }
      });

      $("#contract_grid").on("click", ".delete-btn", function () {
        const status = $(this).data("status");
        if(status==="Success" || status==="success"){
        handleDeleteShow()
        const id = $(this).data("id");
        const name = getFilenameFromURL($(this).data("name"));
        setTimeout(function() {
          $("#btn_delete").attr("data-id", id);
          $("#document_name").html(name);
        }, 200);}
        else {
          const targetElement = document.getElementById("app_err");
          if (targetElement) {
                    ReactDOM.unmountComponentAtNode(targetElement);
          }
          ReactDOM.render(<Message type='info' message="Please wait while the document is being processed." />, targetElement);
        }
        });
  
      $("#contract_grid").on("click", ".askgenie-btn", function () {
        const id = $(this).data("id");
        const name = $(this).data("name");
        const status = $(this).data("status");
        if(status==="Success" || status==="success"){
          handleAskgenie(id, name);
        }else{
          const targetElement = document.getElementById("app_err");
          if (targetElement) {
                    ReactDOM.unmountComponentAtNode(targetElement);
          }
          ReactDOM.render(<Message type='info' message="Please wait while the document is being processed." />, targetElement);
        }
      });
      return () => {
        table.destroy(); // Clean up DataTable instance
      };
    }
  }, [filteredData]);





    // Search handler
    const handleSearch = (e) => {
      setSearchQuery(e.target.value);
      applyFilters(e.target.value, filters);
    };
  const [files, setFiles] = useState([]);
  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    if (droppedFiles[0]?.size > 20 * 1024 * 1024) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        documentFile: "File size must not exceed 20 MB.",
      }));
      return;
    }
    setFiles((prevFiles) => [...prevFiles, ...droppedFiles]);
    setFormData((prev) => ({ ...prev, documentFile: droppedFiles[0] }));
    setErrors((prevErrors) => ({ ...prevErrors, documentFile: "" }));
  };

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const file = selectedFiles[0];
    if (file?.size > 20 * 1024 * 1024) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        documentFile: "File size must not exceed 20 MB.",
      }));
      return;
    }
    setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    setFormData((prev) => ({ ...prev, documentFile: file }));
    setErrors((prevErrors) => ({ ...prevErrors, documentFile: "" }));
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleSelectChange = (selectedOption, actionMeta) => {
    const { name } = actionMeta;
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption,
    }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.licensor) newErrors.licensor = "Licensor is required.";
    if (!formData.contractNo) newErrors.contractNo = "Contract No is required.";
    if (!formData.documentType)
      newErrors.documentType = "Document Type is required.";
   // if (!formData.masterFile) newErrors.masterFile = "Master File is required.";
    if (!formData.documentType)
      newErrors.documentType = "Document Type is required.";
    if (
      formData.documentType === "Amendment" &&
      !formData.masterFile
    ) {
      newErrors.masterFile = "Master File is required for Amendment.";
    }

    if (!formData.documentFile) newErrors.documentFile = "Document File is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveDocuemnt = async (data) => {
    $("#uploaderror").html("")
    let authToken =  localStorage.getItem("jsondata")
    if (authToken) {
      const jsonData = JSON.parse(authToken);
      authToken = jsonData.accessToken || "";
   }
    // try {
      $("#process-loader").removeClass("d-none")
      const response = await axios.post(API_SERVER_URL+'contracts/',data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': 'Bearer ' + authToken,
        },
      });
      if(response.data.code === API_SUCCESS_RESPONSE_CREATED_CODE){
        const targetElement = document.getElementById("app_err");
        if (targetElement) {
          ReactDOM.unmountComponentAtNode(targetElement);
        }
        ReactDOM.render(<Message type='success' message={response.data.data.message} />, targetElement);
        handleClose()
        fetchData()
      }else {
        $("#process-loader").addClass("d-none")
        $("#uploaderror").html(response.data.message)
      }
    // } catch (error) {
    //   console.error('Error uploading the file:', error);
    // }
    return;
  }
  
  const resetForm = (e) => {

     const accessCheck = validatePageAccess(pageName, "add", "You don't have permission to add document.");
        if (accessCheck) {
          setAccessModal(accessCheck);
          return;
        }

    handleShow()
    setFiles([])
    setFormData(initialState); // Reset form fields to initial state
  }

  const handleSubmit = (e) => {
    
    const logged_user_name = getUsername();
    e.preventDefault();
    // $('#uploadcontract-modal').modal('hide');
    if (!validateForm()) return;
    $(".page_pro_load").show();
    // const uniqueId = `doc-${Math.random().toString(36).substr(2, 9)}`;
    // const uniqueId = Math.floor(100000000000 + Math.random() * 900000000000);
    const timestamp = Date.now();  // Current timestamp in milliseconds
    const randomDigits = Math.floor(Math.random() * 1000); // Generate 3 random digits (0-999)
    const uniqueId = "doc_"+parseInt(`${timestamp}${randomDigits}`.substr(0, 12)); // Concatenate and ensure 12 digits
    const formDataapi = new FormData();
    formDataapi.append('file',formData.documentFile);
    formDataapi.append('doc_id',uniqueId);
    formDataapi.append('contract_id',formData.contractNo);
    formDataapi.append('licensor',formData.licensor.value);
    formDataapi.append('document_type',formData.documentType.value);
    if(formData.documentType.value==="Amendment"){
      formDataapi.append('master_doc_id',formData.masterFile.value);
    }else {
      formDataapi.append('master_doc_id',"");
    }  
    formDataapi.append("username",logged_user_name);
    saveDocuemnt(formDataapi)
  };


  async function updateContract(data){
    const logged_user_name = getUsername();
    let request_object = {}
    request_object.licensor = data.licensor.value
    request_object.ContractID = data.contractNo
    request_object.last_edited_by =logged_user_name;

    let apiUrl = `${API_SERVER_URL}contracts/`+data._id+`/`
    const targetElement = document.getElementById("app_err");
    if (targetElement) {
      ReactDOM.unmountComponentAtNode(targetElement);
    }
    let method = "POST"
             try {
                 const result = await apiCall(method,apiUrl,request_object);
                 if(result!==undefined){
                 if (result.code === API_SUCCESS_RESPONSE_CREATED_CODE) {
                  fetchData();
                    ReactDOM.render(<Message type='success' message={result.data.message} />, targetElement);
                 }
              } 
           } catch (error) {
              console.error("Error fetching dropdown data:", error);
            }

//            ReactDOM.render(<Message type='success' message="Testing Message" />, targetElement);
            // Submit logic here
  //          console.log("Form submitted successfully:", formData);
            handleEditClose();

          }
  const handleEditSubmit = (e) => {
    e.preventDefault();
    const validationErrors = {};
    if (!formData.licensor) validationErrors.licensor = "Licensor is required.";
    if (!formData.contractNo) validationErrors.contractNo = "Contract No is required.";
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    updateContract(formData)

  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prevState) => {
      const newFilters = { ...prevState, [filterName]: value };
      applyFilters(newFilters);
      return newFilters;
    });
  };

  const applyFilters = (newFilters) => {
    let filteredDataTemp = data;

      // Multi-select filter for Document Type
  if (newFilters.filterdocumentType && newFilters.filterdocumentType.length > 0) {
    const selectedDocumentTypes = newFilters.filterdocumentType.map(option => option.value);
    filteredDataTemp = filteredDataTemp.filter((doc) =>
      selectedDocumentTypes.some(type => doc.documenttype.includes(type))
    );
  }

  // Multi-select filter for Licensor
  if (newFilters.filterlicensor && newFilters.filterlicensor.length > 0) {
    const selectedLicensors = newFilters.filterlicensor.map(option => option.value);
    filteredDataTemp = filteredDataTemp.filter((doc) =>
      selectedLicensors.some(licensor => doc.licensor.includes(licensor))
    );
  }

  // Multi-select filter for Status
  if (newFilters.filterstatus && newFilters.filterstatus.length > 0) {
    const selectedStatuses = newFilters.filterstatus.map(option => option.value.toLowerCase());
    filteredDataTemp = filteredDataTemp.filter((doc) =>
      selectedStatuses.some(status => doc.status.StatusCode.toLowerCase().includes(status))
    );
  }

  
    if (newFilters.filterTextSearch) {
      const searchTerm = newFilters.filterTextSearch.toLowerCase();
    
      filteredDataTemp = filteredDataTemp.filter((doc) => {
        // Recursive function to check all fields
        const checkFields = (obj) => {
          return Object.keys(obj).some((key) => {
            if (typeof obj[key] === 'object' && obj[key] !== null) {
              // Recursively check nested objects
              return checkFields(obj[key]);
            } else if (typeof obj[key] === 'string') {
              // Check if string contains the search term
              return obj[key].toLowerCase().includes(searchTerm);
            }
            return false;
          });
        };
    // const updatedText = text.replace(/days/g, searchTerm);

        // Apply the recursive check to the main document object
        return checkFields(doc) ||
          doc?.status?.StatusCode?.toLowerCase().includes(searchTerm) ||
          doc?.items?.LicensorLegalName?.value?.toLowerCase().includes(searchTerm) ||
          doc?.items?.startdate?.value?.toLowerCase().includes(searchTerm) ||
          doc?.items?.expirationdate?.value?.toLowerCase().includes(searchTerm) ||
          // Ensure "sell-off period" exists and is a string before applying search
          (typeof doc?.items?.["sell-off period"]?.value === 'string' && 
            doc.items["sell-off period"].value.toLowerCase().includes(searchTerm)) ||
          doc?.items?.PayableContact?.value?.toLowerCase().includes(searchTerm);
      });
    }
    setFilteredData(filteredDataTemp);

  };

  const resetFilters = () => {
    setSearchQuery('');
    setFilters({
      filterdocumentType: null,
      filterlicensor: null,
      filterexpiryDate: '',
      filterstatus: null
    });
    setFilteredData(data); // Reset to full data
  };


  return (


    
    <div className="main-content" style={{ paddingTop: 64 }}>
       <HeaderLogo />
      <div className="page-content">
        <div className="container-fluid">
          <div id="table_height">
           
{/* <div class="mb-4 row"><div class="col">
<h4 class="fw-bold mb-1 text-primary">
<CommonIcon iconName="FileText" size={"28px"}  /> Contract List </h4>
<p class="text-muted mb-0">Manage and view all your contract documents</p>
</div></div> */}

<div className="mb-4">
            <div className="d-flex align-items-center mb-2">
              <div className="bg-primary text-white rounded p-2 me-3">
                <CommonIcon iconName="FileText" size={"24px"}  color="#fff" />
              </div>
              <div className="flex-grow-1">
                <h1 className="h3 mb-0 fw-bold">Contract List</h1>
                <p className="text-muted mb-0 small">Manage and view all your contract documents</p>
              </div>
              
    <div className="ms-auto bg-primary d-none" style={{padding:"1px",border:"1px solid #000",position:"absolute", right:"5px",borderRadius:"2px",top:"15px"}}>
    <img src="../images/predatica_logo.png" alt="Logo" height={"20px"}  /> 
    </div>
            </div>  

          </div>

<div id="app_err"></div>

  <AccessDeniedModal
        isOpen={accessModal.isOpen}
        onClose={() => setAccessModal({ isOpen: false })}
        title={accessModal.title}
        message={accessModal.message}
        page={accessModal.page}
        action={accessModal.action}
      />


{hasPageAccess(pageName, "view") === false ? accessDenied() : 
(
<>
      <div className="row g-2 mb-4 mt-4" id="row_search">
            <div className="col-sm-9">
              <div className="search-box">
                <input
                  type="text"
                  className="form-control"
                  id="searchDocuments"
                  placeholder="Search Documents..."  // This should work as expected
                  onChange={(e) => handleFilterChange('filterTextSearch', e.target.value)}
                />
                <i className="grid-search">
                <CommonIcon iconName="Search" size={"14px"}  />
                </i>
              </div>
            </div>
            <div className="col-sm-auto ms-auto">
              <div className="list-grid-nav hstack gap-4">
                <button className="btn btn-light" onClick={fetchData}>
                <CommonIcon iconName="RefreshCw" size={"14px"} color={"#000"} />
                </button>
               <button onClick={resetForm}
                  className="btn btn-primary uploadcontract-modal"
                >
                  <CommonIcon iconName="Upload" size={"14px"} color="#fff" /> Upload
                </button> 

                  {/* <button 
                  className="btn btn-primary uploadcontract-modal"
                  data-bs-toggle="modal"
                  disabled={true}
                >
                  <CommonIcon iconName="Upload" size={"14px"} color="#fff" /> Upload
                </button> */}
                
                <button
                  type="button"
                  id="dropdownMenuLink1"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  className="d-none btn btn-soft-info btn-icon material-shadow-none fs-14"
                >
                  <i className="ri-more-2-fill"></i>
                </button>
                <ul className="dropdown-menu" aria-labelledby="dropdownMenuLink1">
                  <li>
                    <a className="dropdown-item" href="#">
                      Show All
                    </a>
                  </li>
                  <li>
                    <a className="dropdown-item" href="#">
                      Show Last Week
                    </a>
                  </li>
                  <li>
                    <a className="dropdown-item" href="#">
                      Show Last Month
                    </a>
                  </li>
                  <li>
                    <a className="dropdown-item" href="#">
                      Show Last Year
                    </a>
                  </li>
                </ul>
              </div>
            </div>
      </div>

      <div className="row" id="row_filter" >
      {/* <div className="col-lg-1 mt-2">
      <CommonIcon iconName="Filter" size={"14px"}  /> 
      </div> */}
      <div className="col-lg-3">
      <Select
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{
              Option
            }}
            value={filters.filterdocumentType}
            onChange={(selectedOption) => handleFilterChange('filterdocumentType', selectedOption)}
            options={documentTypeOptions}
            placeholder="Document Type"
          />
      </div>
      <div className="col-lg-3">
      <Select
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{
              Option
            }}
            value={filters.filterlicensor}
            onChange={(selectedOption) => handleFilterChange('filterlicensor', selectedOption)}
            options={licensorOptions}
            placeholder="Licensor"
          />
      </div>
      <div className="col-lg-3 mb-2">
      <Select
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{
              Option
            }}
            value={filters.filterstatus}
            onChange={(selectedOption) => handleFilterChange('filterstatus', selectedOption)}
            options={statusoptions}
            placeholder="Status"
          />
      </div>
      </div>


          <div className="row">
            <div className="col-lg-12">
              {/* <div className="card">
                <div className="card-body"> */}
                  <div>
                    {/* DataTable */}
                    {loading ? (
                 <PageLoader />
            ) :  data.length > 0 ?
                (
                <div className="row  mt-4">
                  <div className="col-lg-12">
                    <table
                      id="contract_grid"
                      className="display"
                      style={{ width: "100%" }}
                    >
                      <thead>
                      </thead>
                    </table>
                  </div>
                </div>) : "" }
                  </div>
                </div>
          </div>
          </>)}
         
        </div>
      </div> 
      
      <div
        className="modal fade"
        id="addmemberModal"
        tabIndex="-1"
        aria-labelledby="addmemberModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content border-0">
            <div className="modal-header p-3 bg-warning-subtle">
              <h5 className="modal-title" id="addmemberModalLabel">
                Upload New Contract
              </h5>
              <button
                id="upload_model_close"
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            {/* <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-lg-12">
                    <label className="form-label">Licensor</label>
                    <Select
                        name="licensor"
                        options={licensorOptions}
                        value={formData.licensor}
                        onChange={handleSelectChange}
                        placeholder="Select a Licensor"
                      />
                    {errors.licensor && <div className="text-danger">{errors.licensor}</div>}
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label">Contract No</label>
                    <input
                      type="text"
                      name="contractNo"
                      className="form-control"
                      value={formData.contractNo}
                      onChange={handleInputChange}
                    />
                    {errors.contractNo && <div className="text-danger">{errors.contractNo}</div>}
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label">Document Type</label>

                    <Select
            name="documentType"
            value={formData.documentType}
            onChange={handleSelectChange}
          options={documentTypeOptions}
          placeholder="Docuemnt Type"
        />
                   
                    {errors.documentType && <div className="text-danger">{errors.documentType}</div>}
                  </div>
                  { formData.documentType !== null && formData.documentType !== "" && formData.documentType.value === "Amendment" && (
                  <div className="col-lg-12">
                    <label className="form-label">Master File</label>

                      <Select
                        name="masterFile"
                        value={formData.masterFile}
                        onChange={handleSelectChange}
                        options={masterDocumentOptions}
                        placeholder="Select Master File"
                    />
                    {errors.masterFile && (
                      <div className="text-danger">{errors.masterFile}</div>
                    )}
                  </div>
                )}
                  <div className="col-lg-12">
                    <label className="form-label">Document File</label>
                    <div
                      className="dropzone"
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      style={{
                        border: "2px dashed #ddd",
                        padding: "20px",
                        textAlign: "center",
                      }}
                    >
                      <input
                        type="file"
                        id="fileInput"
                        style={{ display: "none" }}
                        onChange={handleFileSelect}
                        accept="application/pdf"
                      />
                      <div className="mb-3">
                        <i className="display-4 text-muted">
                        <CommonIcon iconName="Upload" size={"34px"} />
                        </i>
                      </div>
                      <label htmlFor="fileInput" style={{ cursor: "pointer" }}>
                        Drop files here or click to upload
                      </label>
                    </div>
                    <ul style={{ marginTop: "20px" }}>
                    {files.map((file, index) => (
                    <span>{file.name}</span>
                    ))}
                  </ul>
                    {errors.documentFile && <div className="text-danger">{errors.documentFile}</div>}
                  </div>
                <div className="page_pro_load mb-2">{processLoader("Uploading PDF inprogress, please wait....")}</div>
                <div id="uploaderror"></div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" data-bs-dismiss="modal"><i className="ri-close-line align-bottom me-1"></i> Close</button>
                  <button type="submit" className="btn btn-primary">
                    Save
                  </button>
                </div>
              </form>
            </div> */}
          </div>
        </div>
      </div>

      </div>

      <Modal show={deletedocumentmodal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title><div className="page-title" id="confirmationModalLabel">
                Delete
              </div></Modal.Title>
        </Modal.Header>
        <Modal.Body>
        <label class="form-label">Are you sure want to delete <span id="document_name"></span> document?</label>
          </Modal.Body>
        <Modal.Footer>
        <button type="button" onClick={handleDeleteClose} className="btn btn-light" data-bs-dismiss="modal"><CommonIcon iconName="X" size={"14px"} /> Close</button>
                  <button onClick={handleDelete} id="btn_delete" data-id="1111" type="submit" className="btn btn-primary">
                    <CommonIcon iconName="Trash" size={"14px"} color="#fff" /> Delete
                  </button>
        </Modal.Footer>
      </Modal>

      <Modal show={adddocumentmodal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title> 
                Upload New Contract
           </Modal.Title>
           <p className="modal-desc">
               Provide details and upload the contract file.
            </p>
        </Modal.Header>
        <Modal.Body>
        <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-lg-12">
                    <label className="form-label">Licensor</label>
                    <Select
                        name="licensor"
                        options={licensorOptions}
                        value={formData.licensor}
                        onChange={handleSelectChange}
                        placeholder="Select a Licensor"
                      />
                    {errors.licensor && <div className="text-danger">{errors.licensor}</div>}
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label">Contract No</label>
                    <input
                      type="text"
                      name="contractNo"
                      className="form-control"
                      value={formData.contractNo}
                      onChange={handleInputChange}
                    />
                    {errors.contractNo && <div className="text-danger">{errors.contractNo}</div>}
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label">Document Type</label>

                    <Select
            name="documentType"
            value={formData.documentType}
            onChange={handleSelectChange}
          options={documentTypeOptions}
          placeholder="Document Type"
        />
                   
                    {errors.documentType && <div className="text-danger">{errors.documentType}</div>}
                  </div>
                  { formData.documentType !== null && formData.documentType !== "" && formData.documentType.value === "Amendment" && (
                  <div className="col-lg-12">
                    <label className="form-label">Master File</label>
                      <Select
                        name="masterFile"
                        value={formData.masterFile}
                        onChange={handleSelectChange}
                        options={masterDocumentOptions.filter(doc => doc.licensor === formData.licensor.label)}
                        placeholder="Select Master File"
                    />
                    {errors.masterFile && (
                      <div className="text-danger">{errors.masterFile}</div>
                    )}
                  </div>
                )}
                  <div className="col-lg-12">
                    <label className="form-label">Document File</label>
                    <div
                      className="dropzone"
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      style={{
                        border: "2px dashed #ddd",
                        padding: "20px",
                        textAlign: "center",
                      }}
                    >
                      <input
                        type="file"
                        id="fileInput"
                        style={{ display: "none" }}
                        onChange={handleFileSelect}
                        accept="application/pdf"
                      />
                      <div className="mb-3">
                        <i className="display-4 text-muted">
                        <CommonIcon iconName="Upload" size={"34px"} />
                        </i>
                      </div>
                      <label htmlFor="fileInput" style={{ cursor: "pointer" }}>
                        Drop files here or click to upload
                      </label>
                    </div>
                    <ul style={{ marginTop: "20px",wordBreak:"break-all" }}>
                    {files.map((file, index) => (
                    <span>{file.name}</span>
                    ))}
                  </ul>
                    {errors.documentFile && <div className="text-danger">{errors.documentFile}</div>}
                  </div>
                <div className="page_pro_load mb-2">{
                <ProcessLoader pmMessage={"PDF upload in progress, please wait..."}  />
                }</div>
                <div id="uploaderror"></div>
                </div>
                {/* <div className="modal-footer">
                  <button type="button" className="btn btn-light" data-bs-dismiss="modal"><i className="ri-close-line align-bottom me-1"></i> Close</button>
                  <button type="submit" className="btn btn-primary">
                    Save
                  </button>
                </div> */}
              </form>
            </div>
        </Modal.Body>
        <Modal.Footer>
        <button type="button" onClick={handleClose} className="btn btn-light" data-bs-dismiss="modal"><i className="ri-close-line align-bottom me-1"></i> Close</button>
              <button onClick={handleSubmit} type="submit" className="btn btn-primary">
                    Save
               </button>
        </Modal.Footer>
      </Modal>

      <Modal show={editdocumentmodal} onHide={handleEditClose}>
        <Modal.Header closeButton>
          <Modal.Title> 
                Edit Contract
           </Modal.Title>
           <p className="modal-desc">
           Update the licensor, contract number as needed.
            </p>
        </Modal.Header>
        <Modal.Body>
        <div className="modal-body">
              <form onSubmit={handleEditSubmit}>
                <div className="row g-3">
                <div className="col-lg-12">
                    <label className="form-label">Document Name</label>
                    <br/>{formData.documentFile}
                  </div>
                  <div className="col-lg-12">
                    <label className="form-label">Licensor</label>
                    <Select
                        name="licensor"
                        options={licensorOptions}
                        value={formData.licensor}
                        onChange={handleSelectChange}
                        placeholder="Select a Licensor"
                      />
                    {errors.licensor && <div className="text-danger">{errors.licensor}</div>}
                  </div>
                  <div className="col-lg-12">
                    <label className="form-label">Contract No</label>
                    <input
                      type="text"
                      name="contractNo"
                      className="form-control"
                      value={formData.contractNo}
                      onChange={handleInputChange}
                    />
                    {errors.contractNo && <div className="text-danger">{errors.contractNo}</div>}
                  </div>
                <div id="uploaderror"></div>
                </div>
              </form>
            </div>
        </Modal.Body>
        <Modal.Footer>
        <button type="button" onClick={handleEditClose} className="btn btn-light" data-bs-dismiss="modal"><i className="ri-close-line align-bottom me-1"></i> Close</button>
              <button onClick={handleEditSubmit} type="submit" className="btn btn-primary">
                    Save
               </button>
        </Modal.Footer>
    </Modal>

    </div> 
  );
};